import { Box, BoxProps, Skeleton, Tooltip } from "@chakra-ui/react";
import { QuestionIcon } from "@chakra-ui/icons";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";

const baseFragment = gql(/* GraphQL */ `
  fragment RetailerLocationBaseNameFragment on RetailerLocationWithIdentifier {
    id
    displayName
    isDisplayNameInferred
    isUnknown
  }
`);

const fragment = gql(/* GraphQL */ `
  fragment RetailerLocationNameFragment on RetailerLocationWithIdentifier {
    id
    ...RetailerLocationBaseNameFragment
    retailerLocation {
      id
      retailer {
        id
        nameWithUniqueSuffix
      }
    }
  }
`);

type BaseProps = {
  isLoading?: boolean;
  location: FragmentType<typeof baseFragment> | null | undefined;
};

export function BaseLocationName({ isLoading, location }: BaseProps) {
  const rlwi = getFragmentData(baseFragment, location);

  const { displayName, isDisplayNameInferred, isUnknown } = rlwi || {};

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Box
        data-is-inferred={isDisplayNameInferred || undefined}
        data-is-unknown={isUnknown || undefined}
        sx={{ "&[data-is-inferred], &[data-is-unknown]": { opacity: 0.5 } }}
        noOfLines={2}
      >
        <Skeleton isLoaded={!!displayName || !isLoading} display="inline">
          {displayName || "                 "}
        </Skeleton>
      </Box>

      {isUnknown && (
        <Tooltip label="Location details unknown" hasArrow openDelay={500}>
          <QuestionIcon color="yellow.400" boxSize={4} />
        </Tooltip>
      )}
    </Box>
  );
}

type Props = BoxProps & {
  isLoading?: boolean;
  location: FragmentType<typeof fragment> | null | undefined;
};

export default function LocationName({ isLoading, location, ...rest }: Props) {
  const rlwi = getFragmentData(fragment, location);

  const rl = rlwi?.retailerLocation;

  return (
    <Box
      display="inline-flex"
      flexFlow="column"
      maxW="100%"
      lineHeight="normal"
      {...rest}
    >
      <Box fontSize=".75em" fontWeight={500} isTruncated opacity={0.65}>
        <Skeleton
          isLoaded={!!rl?.retailer?.nameWithUniqueSuffix || !isLoading}
          display="inline"
        >
          {rl?.retailer?.nameWithUniqueSuffix ||
            "                              "}
        </Skeleton>
      </Box>

      <BaseLocationName isLoading={isLoading} location={rlwi} />
    </Box>
  );
}
